import { WorkflowExecution } from '@epilot/customer-portal-client'

import { Workflow, WorkflowStep } from 'utils/WorkflowUtils'

import { Condition, Edge, FlowExecution, Phase, Task } from '../flow/types'

type GetNextNodeResult = {
  nextNodeIds: string[]
  reachedEnd: boolean
}

/**
 * Determines the next node to traverse to from the current node.
 * Handles phases, tasks, conditional branching, and phase exits.
 * Filters out loop edges (edges starting with 'loop-edge-') to prevent infinite loops.
 */
const getNextNodes = (
  currentNodeId: string,
  edges: Edge[],
  taskMap: Map<string, Task>,
  phaseMap: Map<string, Phase>,
  conditionMap: Map<string, Condition>
): GetNextNodeResult => {
  // Filter out loop edges to prevent infinite loops
  const nonLoopEdges = edges.filter((edge) => !edge.id.startsWith('loop-edge-'))

  // Handle phase nodes
  if (phaseMap.has(currentNodeId)) {
    // Find the first task inside this phase
    const firstTaskEdge = nonLoopEdges.find((edge) => {
      if (edge.from_id !== currentNodeId) return false
      const targetTask = taskMap.get(edge.to_id)

      return targetTask?.phase_id === currentNodeId
    })

    if (firstTaskEdge) {
      return { nextNodeIds: [firstTaskEdge.to_id], reachedEnd: false }
    }

    // No tasks in phase, try to find continuation edge
    const continuationEdge = nonLoopEdges.find(
      (edge) => edge.from_id === currentNodeId
    )

    if (continuationEdge) {
      return { nextNodeIds: [continuationEdge.to_id], reachedEnd: false }
    }

    // No edges found, end of flow
    return { nextNodeIds: [], reachedEnd: true }
  }

  // Handle task nodes
  const task = taskMap.get(currentNodeId)

  if (!task) {
    return { nextNodeIds: [], reachedEnd: true }
  }

  // Find outgoing edges from this task (excluding loop edges)
  const outgoingEdges = nonLoopEdges.filter((edge) => edge.from_id === task.id)

  // If no outgoing edges from task, check if we need to exit the phase
  if (
    outgoingEdges.length === 0 ||
    (outgoingEdges.length === 1 && !outgoingEdges[0].to_id)
  ) {
    if (task.phase_id) {
      // Find the edge that goes out of the phase
      const phaseEdges = nonLoopEdges.filter(
        (edge) => edge.from_id === task.phase_id
      )

      const phaseOutgoingEdge = phaseEdges.find((edge) => {
        const targetPhase = phaseMap.get(edge.to_id)

        if (targetPhase) return true
        const targetTask = taskMap.get(edge.to_id)

        return targetTask?.phase_id !== task.phase_id
      })

      if (phaseOutgoingEdge) {
        return { nextNodeIds: [phaseOutgoingEdge.to_id], reachedEnd: false }
      }
    }

    // Reached the end
    return { nextNodeIds: [], reachedEnd: true }
  }

  // If only one outgoing edge, follow it
  if (outgoingEdges.length === 1) {
    return { nextNodeIds: [outgoingEdges[0].to_id], reachedEnd: false }
  }

  // Multiple outgoing edges - this is a conditional branching point
  // Find ALL winning branches (edges with conditions that have is_met = true)
  // This supports parallel branches where multiple conditions can be met simultaneously
  const winnerEdges = outgoingEdges.filter((edge) => {
    if (!edge.condition_id) {
      return false
    }

    const condition = conditionMap.get(edge.condition_id)

    return condition?.is_met === true
  })

  // If we found winning branches, follow all of them (parallel branch support)
  if (winnerEdges.length > 0) {
    const nextNodeIds: string[] = []

    for (const winnerEdge of winnerEdges) {
      if (winnerEdge.to_id) {
        nextNodeIds.push(winnerEdge.to_id)
      } else if (!winnerEdge.to_id && task.phase_id) {
        // decision task inside the phase where the selected edge leads to the end of the phase
        // Find the edge that goes out of the phase
        const phaseEdges = nonLoopEdges.filter(
          (edge) => edge.from_id === task.phase_id
        )

        const phaseOutgoingEdge = phaseEdges.find((edge) => {
          const targetPhase = phaseMap.get(edge.to_id)

          if (targetPhase) return true
          const targetTask = taskMap.get(edge.to_id)

          return targetTask?.phase_id !== task.phase_id
        })

        if (phaseOutgoingEdge) {
          nextNodeIds.push(phaseOutgoingEdge.to_id)
        }
      }
    }

    if (nextNodeIds.length > 0) {
      return { nextNodeIds, reachedEnd: false }
    }
  }

  // If no winner branch found (conditional task not evaluated), stop here
  // This means the decision hasn't been made yet
  return { nextNodeIds: [], reachedEnd: false }
}

/**
 * Determines the active execution path through the flow graph
 * by traversing edges starting from 'trigger' node, following the winner branch
 * when encountering conditional tasks with multiple outgoing edges
 */
export const getActiveFlowPath = (
  flow: FlowExecution
): { tasks: Task[]; isPathComplete: boolean; reachedTaskIds: Set<string> } => {
  if (!flow?.tasks || !flow?.edges) {
    return { tasks: [], isPathComplete: false, reachedTaskIds: new Set() }
  }

  const { tasks, edges, phases } = flow

  // Create task lookup map
  const taskMap = new Map(tasks.map((task) => [task.id, task]))

  // Create phase lookup map
  const phaseMap = new Map(phases?.map((phase) => [phase.id, phase]) || [])

  // Create condition lookup map from all tasks
  const conditionMap = new Map<string, Condition>()

  tasks.forEach((task) => {
    task.conditions?.forEach((condition) => {
      conditionMap.set(condition.id, condition)
    })
  })

  // Track visited to avoid cycles
  const visited = new Set<string>()
  const activePath: Task[] = []
  let isPathComplete = true
  const reachedTaskIds = new Set<string>()

  // Track whether each node has been "reached" in the execution.
  // A node is reached if the traversal arrives at it through completed/skipped predecessors.
  // This allows PENDING tasks whose predecessors are all done to be shown as active
  // in the portal (e.g. installer tasks that don't require a manual "start" by an internal user).
  const nodeReachedMap = new Map<string, boolean>()

  // BFS traversal to support parallel branches
  // When a decision task has multiple winning conditions, all branches are followed
  const queue: string[] = []

  // Start traversal from the 'trigger' node
  const triggerEdge = edges.find((edge) => edge.from_id === 'trigger')

  if (triggerEdge?.to_id) {
    queue.push(triggerEdge.to_id)
    nodeReachedMap.set(triggerEdge.to_id, true)
  }

  while (queue.length > 0) {
    const currentNodeId = queue.shift()!

    // Check if already visited to avoid cycles
    if (visited.has(currentNodeId)) {
      continue
    }

    visited.add(currentNodeId)

    const currentReached = nodeReachedMap.get(currentNodeId) ?? false

    // Add task to active path if it's ECP or Installer enabled and it's a task node
    const task = taskMap.get(currentNodeId)

    if (task?.ecp?.enabled || task?.installer?.enabled) {
      activePath.push(task)

      if (currentReached) {
        reachedTaskIds.add(task.id)
      }
    }

    // Determine if successors should be marked as "reached".
    // Phase nodes and non-task nodes propagate reached status unchanged.
    // Task nodes only propagate reached if they are completed/skipped —
    // a task that is still pending or in-progress blocks successor reachability.
    const isPhaseNode = phaseMap.has(currentNodeId)
    let successorsReached: boolean

    if (isPhaseNode || !task) {
      successorsReached = currentReached
    } else {
      successorsReached =
        currentReached &&
        (task.status === 'COMPLETED' || task.status === 'SKIPPED')
    }

    // Get the next nodes using the extracted function
    // For parallel branches, this returns multiple next node IDs
    const { nextNodeIds, reachedEnd } = getNextNodes(
      currentNodeId,
      edges,
      taskMap,
      phaseMap,
      conditionMap
    )

    if (!reachedEnd && nextNodeIds.length === 0) {
      // This means we stopped at an unevaluated decision task
      isPathComplete = false
    }

    // Enqueue all next nodes (supports parallel branches)
    for (const nextId of nextNodeIds) {
      // Update reached status using OR — if any path marks a node as reached, keep it
      const existingReached = nodeReachedMap.get(nextId) ?? false

      nodeReachedMap.set(nextId, existingReached || successorsReached)

      if (!visited.has(nextId)) {
        queue.push(nextId)
      }
    }
  }

  return { tasks: activePath, isPathComplete, reachedTaskIds }
}

/**
 * Transform a task into a step
 */
const transformTaskToStep = (
  task: Task,
  index: number,
  isReached?: boolean
): WorkflowStep => {
  let stepStatus: 'COMPLETED' | 'SKIPPED' | 'IN_PROGRESS' = 'IN_PROGRESS'

  if (task.status === 'COMPLETED' || task.status === 'SKIPPED') {
    stepStatus = 'COMPLETED'
  } else if (
    task.status === 'PENDING' ||
    task.status === 'CONDITION_PENDING' ||
    task.status === 'IN_PROGRESS'
  ) {
    stepStatus = 'IN_PROGRESS'
  }

  // A task is active when it has been reached in the flow execution.
  // Non-pending/non-condition-pending tasks are always active.
  // PENDING tasks are also active when they've been reached — i.e. all their
  // predecessor tasks in the flow are completed/skipped. This ensures that
  // portal users (e.g. installers) can interact with tasks that are ready for
  // them without requiring an internal user to manually start the task first.
  const isActive =
    (task.status !== 'PENDING' && task.status !== 'CONDITION_PENDING') ||
    (task.status === 'PENDING' && isReached === true)

  const step: WorkflowStep = {
    assignedTo: task.assigned_to,
    ecp: {
      description: task.ecp?.description,
      enabled: task.ecp?.enabled || false,
      label: task.ecp?.label || task.name
    },
    installer: {
      description: task.installer?.description,
      enabled: task.installer?.enabled || false,
      label: task.installer?.label || task.name
    },
    id: task.id,
    isActive,
    journey: task.journey
      ? {
          id: task.journey.id,
          journeyId: task.journey.journeyId,
          complete_task_automatically: task.journey.complete_task_automatically,
          name: task.journey.name
        }
      : undefined,
    label: task.name,
    lastUpdated: (task.analytics as any)?.completed_at || task.updated_at,
    name: task.name,
    order: index,
    status: stepStatus,
    type: 'STEP' as const
  }

  return step
}

/**
 * Chronological rank of a step in the timeline lifecycle:
 *   0 — completed/skipped (already happened)
 *   1 — active/in-progress (happening now)
 *   2 — pending/not yet reached (hasn't happened)
 * Keeping done steps as the prefix mirrors the progress metaphor and the
 * assumption in getWorkflowProgressInformation that completed steps come first.
 */
const getStepChronologicalRank = (step: WorkflowStep): number => {
  if (step.status === 'COMPLETED' || step.status === 'SKIPPED') {
    return 0
  }

  return step.isActive ? 1 : 2
}

const getStepTimestamp = (step: WorkflowStep): number => {
  const time = step.lastUpdated ? new Date(step.lastUpdated).getTime() : NaN

  return Number.isNaN(time) ? 0 : time
}

/**
 * Order steps chronologically for the portal timeline.
 *
 * getActiveFlowPath traverses the flow graph breadth-first, so with parallel
 * branches the raw step order reflects graph depth, not execution time — which
 * makes completed steps appear out of sequence (STABLE360-12198). Re-order
 * completed steps by their completion timestamp (the same value rendered as
 * "vor X Minuten"), then keep active and pending steps after them in their
 * original graph-traversal order.
 */
const sortStepsChronologically = (steps: WorkflowStep[]): WorkflowStep[] =>
  steps
    .map((step, index) => ({ step, index }))
    .sort((a, b) => {
      const rankDiff =
        getStepChronologicalRank(a.step) - getStepChronologicalRank(b.step)

      if (rankDiff !== 0) {
        return rankDiff
      }

      // Only completed steps are re-sorted by time; active/pending steps have
      // no meaningful completion timestamp and keep their graph order.
      if (getStepChronologicalRank(a.step) === 0) {
        const timeDiff = getStepTimestamp(a.step) - getStepTimestamp(b.step)

        if (timeDiff !== 0) {
          return timeDiff
        }
      }

      return a.index - b.index
    })
    .map(({ step }) => step)

const hasPortalEnabledTask = (flow: FlowExecution): boolean => {
  return (
    flow.tasks?.some((task) => task.ecp?.enabled || task.installer?.enabled) ??
    false
  )
}

const getPortalVisibility = (flow: FlowExecution): boolean => {
  return flow.available_in_ecp ?? hasPortalEnabledTask(flow)
}

/**
 * Transform a flow execution into a workflow
 */
const transformToWorkflow = (flow: FlowExecution): Workflow => {
  let workflowStatus = 'STARTED'

  if (flow.status === 'COMPLETED') {
    workflowStatus = 'DONE'
  } else if (flow.status === 'CLOSED' || flow.status === 'PAUSED') {
    workflowStatus = 'CLOSED'
  } else if (flow.status === 'STARTED') {
    workflowStatus = 'STARTED'
  }

  const workflow: Workflow = {
    assignedTo: flow.assigned_to || [],
    configuredClosingReasonSnapshot:
      flow.closing_reason?.configured_reasons || [],
    contexts:
      flow.contexts?.map((ctx) => ({
        id: ctx.entity_id,
        schema: ctx.entity_schema,
        title: ''
      })) || [],
    creationTime: flow.created_at,
    definitionId: flow.flow_template_id,
    enableECPWorkflow: getPortalVisibility(flow),
    flow: [],
    id: flow.wfExecId || flow.id,
    lastUpdateTime: flow.updated_at,
    name: flow.name,
    nextOpenStep: {
      entityRefId: '',
      id: ''
    },
    orgId: flow.org_id || flow.orgId,
    selectedClosingReasons: null,
    status: workflowStatus,
    trigger: flow.trigger?.type || 'MANUAL',
    updateEntityAttributes: [],
    version: flow.version || 3
  }

  return workflow
}

/**
 * Transform a flow execution's active path into a steps array
 */
const transformToSteps = (flow: FlowExecution) => {
  const {
    tasks: activeTasks,
    isPathComplete,
    reachedTaskIds
  } = getActiveFlowPath(flow)

  if (activeTasks.length === 0) {
    return { steps: [], isPathComplete: true }
  }

  const steps = activeTasks.map((task, index) =>
    transformTaskToStep(task, index, reachedTaskIds.has(task.id))
  )

  const sortedSteps = sortStepsChronologically(steps)

  // Keep `order` aligned with the final rendered position.
  sortedSteps.forEach((step, index) => {
    step.order = index
  })

  return { steps: sortedSteps, isPathComplete }
}

/**
 * Check if a flow execution is version 3
 */
export const isWorkflowV3 = (
  data: WorkflowExecution | FlowExecution
): data is FlowExecution => {
  return data?.version === 3 && !!data?.tasks
}

/**
 * Convert a flow execution into a linear workflow
 */
export const convertToLinearWorkflow = (flow: FlowExecution): Workflow => {
  const workflow = transformToWorkflow(flow)
  const { steps, isPathComplete } = transformToSteps(flow)

  return {
    ...workflow,
    isConvertedToLinearWf: true,
    isActivePathComplete: isPathComplete,
    flow: [...steps] as unknown as Workflow['flow']
  }
}
